import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../SharedControlPanel.scss';
import './CaptioningPanel.scss';
import { DefaultProjectUser, ProjectUser, ProjectUsers } from '../../../../../types/ProjectUser';
import TickIcon from '../../../../../images/tick-destination-icon.png';
import { InviteProjectUserModal } from '../../../../Modals/InviteProjectUserModal/InviteProjectUserModal';
import useProject from '../../../../../hooks/useProject';
import PageIcon from '../../../../../images/chat-panel-Icon.png';
import { PopupModal } from '../../../../Modals/PopupModal/PopupModal';
import { CaptionModeEnum } from '../../../../../types/enums/CaptionModeEnum';
import { UserTypeEnum } from '../../../../../types/enums/UserTypesEnum';
import CaptionModeInput from '../../../../InputComponents/CaptionModeInput/CaptionModeInput';
import OutputModeInput from '../../../../InputComponents/OutputModeInput/OutputModeInput';
import AsrModeInput from '../../../../InputComponents/AsrModeInput/AsrModeInput';
import LineInput from '../../../../InputComponents/LineInput/LineInput';
import Help from '../../../../Help/Help';

type CaptioningPanelProps = {
  projectUsers: ProjectUsers;
  isReadOnly: boolean;
};

const CaptioningPanel = ({ projectUsers, isReadOnly }: CaptioningPanelProps) => {
  const { t } = useTranslation();
  const { project, updateStream } = useProject();
  const maxNumberOfCaptioners = 4;
  const [isModalOpen, setIsModalOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showRetrievingStreamTextName, setShowRetrievingStreamTextName] = useState(false);
  const [showCaptionOverlayInstructions, setShowCaptionOverlayInstructions] = useState(false);
  const [selectedProjectUser, setSelectedProjectUser] = useState<ProjectUser>({
    ...DefaultProjectUser,
    userType: UserTypeEnum.Captioner,
  });

  useEffect(() => {
    if (project?.encoder?.captionMode !== CaptionModeEnum.None && !project?.streamTextEventName) {
      setShowRetrievingStreamTextName(true);
    } else {
      setShowRetrievingStreamTextName(false);
    }
  }, [project]);

  const isMAC = (): boolean => navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  if (showCaptionOverlayInstructions) {
    return (
      <PopupModal
        isOpen={true}
        handleCloseModal={() => setShowCaptionOverlayInstructions(false)}
        title={`producer.control-panel.captioning.caption-ovarlay-model-title-${
          isMAC() ? 'mac' : 'windows'
        }`}
        content={t(
          `producer.control-panel.captioning.caption-ovarlay-model-content-${
            isMAC() ? 'mac' : 'windows'
          }`,
        )}
      />
    );
  }

  const openInviteProjectUserModal = (name = '', emailAddress = '', id = '', accessToken = '') => {
    if (project?.encoder?.captionMode === CaptionModeEnum.None) {
      updateStream({
        captionMode: CaptionModeEnum.Open,
      });
    }

    setSelectedProjectUser({
      ...DefaultProjectUser,
      id: id,
      name: name,
      emailAddress: emailAddress,
      projectId: project?.id ?? '',
      userType: UserTypeEnum.Captioner,
      accessToken: accessToken,
    });
    setIsModalOpen(true);
  };

  const getCaptionersFromProjectUsers = (): ProjectUsers => {
    if (!projectUsers || projectUsers.length === 0) {
      return [];
    }

    const users = projectUsers.filter(function (obj) {
      return obj.userType === UserTypeEnum.Captioner;
    });

    return users || [];
  };

  const createAddCaptionersButtons = () => {
    if (isReadOnly) {
      return null;
    }
    const addCaptionersButtons = [];
    const numberOfButtonsToCreate = maxNumberOfCaptioners - getCaptionersFromProjectUsers().length;
    for (let index = 0; index < numberOfButtonsToCreate; index++) {
      addCaptionersButtons.push(
        <button
          key={index}
          className="control-panel-section-box control-panel-section-box-new"
          onClick={() => openInviteProjectUserModal()}
          disabled={isReadOnly}
        >
          <span className="control-panel-section-box-text">
            + {t('producer.control-panel.captioning.invite-captioner-button')}
          </span>
        </button>,
      );
    }
    return addCaptionersButtons;
  };

  const getTextPlayerUrl = () => {
    return `/text-player.html?token=${project?.stream?.textPlayerToken}`;
  };  

  return (
    <div className="captioning-panel">
      <div className="captioning-section">
        {getCaptionersFromProjectUsers().map((projectUser: ProjectUser) => (
          <button
            key={projectUser?.id}
            className="control-panel-section-box"
            onClick={() =>
              openInviteProjectUserModal(
                projectUser?.name,
                projectUser?.emailAddress,
                projectUser?.id,
                projectUser?.accessToken,
              )
            }
            disabled={isReadOnly}
          >
            <span className="control-panel-section-box-text">{projectUser?.name}</span>
            <img src={TickIcon} alt="tick" className="control-panel-section-box-tick" />
          </button>
        ))}
        {createAddCaptionersButtons()}
        <div className="captioning-panel-section-footer">
          <p>
            <span className="captioning-panel-section-footer-title">
              {t('producer.control-panel.captioning.note')}{' '}
            </span>
            <span className="captioning-panel-section-footer-text">
              {t('producer.control-panel.captioning.note-text')}
              <Help
                title="Invite Captioner"
                text="Currently captioners need to have a Streamtext account to send captions via AccessLOOP.  They should not look for this event in their own Streamtext account or set up any alternative Streamtext event - this is done in the backend of AccessLOOP.<br/><br/>They cannot send captions without joining on this event via the invitation they will receive via email. Alternatively, once you have added them you can use the 'Copy Invite Link' button and send this to them manually."
              />
            </span>
          </p>
        </div>
      </div>

      <hr className="control-panel-section-divider" />

      <div className="captioning-section captioning-settings-section">
        <div className="captioning-settings-position captioning-settings-position-block-with-title">
          <div className="captioning-settings-open-closed-captions-section">
            <div className="level level-left">
              <CaptionModeInput />
            </div>
          </div>
        </div>
      </div>

      {project?.createdByUser?.isAdmin === true && (
      <>
        <hr className="control-panel-section-divider" />

        <div className="captioning-section captioning-settings-section">
          <div className="captioning-settings-position captioning-settings-position-block-with-title">
            <div className="captioning-settings-open-closed-captions-section">
              <div className="level level-left">
                <OutputModeInput />
              </div>
            </div>
          </div>
        </div>

      </>
      )}

      <hr className="control-panel-section-divider" />

      <div className="captioning-section captioning-settings-section">
        <div className="captioning-settings-position captioning-settings-position-block-with-title">
          <div className="captioning-settings-open-closed-captions-section">
            <LineInput />
          </div>
        </div>
      </div>        

      <hr className="control-panel-section-divider" />

      <div className="captioning-section captioning-settings-section">
        <div className="captioning-settings-position captioning-settings-position-block-with-title">
          <div className="captioning-settings-open-closed-captions-section">
            <div className="level level-left">
              <AsrModeInput />
            </div>
          </div>
        </div>
      </div>

      <hr className="control-panel-section-divider" />

      <div className="captioning-settings-section level">
        <div className="captioning-settings-title">
          <p className="captioning-settings-title-text">
            {t('producer.control-panel.captioning.in-room-captions-display-title')}
          </p>
          <p className="captioning-settings-section-content-value">
            <a className="link" href={getTextPlayerUrl()}>
              {t('producer.control-panel.captioning.in-room-captions-display-link')}
            </a>
            <img
              src={PageIcon}
              alt="Instructions"
              className="captioning-settings-section-download-image"
            />
          </p>
        </div>
      </div>

      {isModalOpen && (
        <InviteProjectUserModal
          isOpen={isModalOpen}
          contentLabel="Invite Captioner Modal"
          selectedProjectUser={selectedProjectUser}
          userType={UserTypeEnum.Captioner}
          handleCloseModal={() => {
            setIsModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default React.memo(CaptioningPanel);
